import { useEffect, useMemo } from 'react';
import { getSortField, getSortValue, sortKey } from '../../../../features/table/helpers';
import { formatDateTimeRangeFromString } from '../../../../helpers/date-time';
import {
  defaultPage,
  defaultPageSize,
  pageKey,
  pageSizeKey,
  viewStyleKey,
  defaultViewStyle,
} from '../../../../helpers/path';
import { useLang } from '../../../../hooks/use-lang';
import { useQueryParams } from '../../../../hooks/use-query-params';
import { FetchReviewsRequest, ReviewsExportFormatType } from '../../../../store/api/reviews-api';
import { useBranchesStore } from '../../../../store/use-branch-store';
import { useReviewsStore } from '../../../../store/use-reviews-store';
import { IdValue, SortType } from '../../../../types/props';
import { useLocation } from 'react-router-dom';
import { ReviewsPath } from '../../../../routes/manager/manager-enums';

const titleRecord: Record<ReviewsPath, string> = {
  branch: 'Филиалы',
  order: 'Заказы',
  action: 'Услуги',
};

export const useReviews = () => {
  const { fetchBranches, isLoadingFetch: isBranchesLoading } = useBranchesStore();

  const {
    fetchReviews,
    fetchReviewsExport,
    reviews,
    isLoadingFetch: isReviewLoading,
    pagination,
  } = useReviewsStore();

  const isLoading = isBranchesLoading || isReviewLoading;
  const { t, currentLang, getLocale } = useLang();

  const { getQueryParams, addQueryParams } = useQueryParams();
  const params = getQueryParams();

  const task_type = useLocation().pathname.split('/')[3];
  const content = params['content'];
  const rating = params['rating'];
  const placeId = params['place_id'];
  const fullName = params['full_name'];
  const contactInfo = params['contact_info'];
  const createdAt = params['created_at'];
  const qr = params['qr'];
  const page = params[pageKey] || defaultPage;
  const pageSize = params[pageSizeKey] || defaultPageSize;
  const sort = params[sortKey];
  const sortField = getSortField(sort);
  const sortDirection = getSortValue(sort);
  const viewStyle = params[viewStyleKey] || defaultViewStyle;

  useEffect(() => {
    fetchBranches({ n: 1000, since_id: 0, with_archive: true });
  }, []);

  const requestParams: FetchReviewsRequest = useMemo(() => {
    const parsedCreatedAt = formatDateTimeRangeFromString(createdAt);

    return {
      content,
      rating,
      qr,
      place_id: placeId ? Number(placeId) : undefined,
      full_name: fullName,
      contact_info: contactInfo,
      from: parsedCreatedAt?.from,
      to: parsedCreatedAt?.to,
      field: sortField,
      dir: sortDirection as SortType,
      is_by_company: true,
      task_type: task_type === ReviewsPath.Branch ? undefined : task_type,
      page,
      page_size: pageSize,
      language: currentLang,
    };
  }, [
    content,
    rating,
    qr,
    placeId,
    fullName,
    contactInfo,
    createdAt,
    sortField,
    sortDirection,
    task_type,
    page,
    pageSize,
    viewStyle,
    currentLang,
  ]);

  useEffect(() => {
    fetchReviews(requestParams);
  }, [requestParams]);

  const handleSelectReviewType = (e: IdValue) => {
    addQueryParams({ review_type: e.id });
  };

  const handleEnd = () => {
    if (isLoading || !pagination || pagination.is_last) return;
    const nextPage = pagination.page + 1;
    fetchReviews({ ...requestParams, page: nextPage, infinite: true });
  };

  const handleExport = (format: ReviewsExportFormatType) => {
    fetchReviewsExport({ ...requestParams, format });
  };

  const title = titleRecord[task_type as ReviewsPath];
  
  return {
    isLoading,
    t,
    getLocale,
    handleSelectReviewType,
    viewStyle,
    handleEnd,
    reviews,
    pagination,
    handleExport,
    title,
  };
};
